import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { SectionCard, formatNumber } from '../../../components/design-system/admin';
import { SettingsField, SettingsToggleRow } from '../../../components/design-system/common';
import type { ContentPackSettings, ContentPackSettingsView } from '../../../common/hooks/use-content-packs-api.hook';
import { KindPicker } from './kind-picker';
import { SHOP_SETTINGS_ROUTE, SOCIAL_ROUTE, platformName } from './content-pack-view';

/**
 * Automatic packs (off until switched on), the monthly limit they stay under,
 * what a pack contains, and where approved pieces go — with whether they can
 * go there today. Every change saves at once.
 */
export const PackSettingsCard: React.FC<{
    view: ContentPackSettingsView;
    onSave: (patch: Partial<ContentPackSettings>) => Promise<void>;
}> = ({ view, onSave }) => {
    const { t } = useTranslation();
    const { settings, usage, estimate, shop, social } = view;
    const [cap, setCap] = useState(String(settings.monthlyCreditCap));
    const [saving, setSaving] = useState(false);
    useEffect(() => setCap(String(settings.monthlyCreditCap)), [settings.monthlyCreditCap]);

    const save = async (patch: Partial<ContentPackSettings>) => {
        setSaving(true);
        try {
            await onSave(patch);
            toast.success(t('CONTENT_PACKS.SETTINGS.SAVED'));
        } catch {
            toast.error(t('CONTENT_PACKS.SETTINGS.SAVE_FAILED'));
        } finally {
            setSaving(false);
        }
    };
    const saveCap = () => {
        const n = Math.max(0, Math.round(Number(cap)));
        if (!Number.isFinite(n) || n === settings.monthlyCreditCap) return setCap(String(settings.monthlyCreditCap));
        void save({ monthlyCreditCap: n });
    };
    const used = Math.min(100, settings.monthlyCreditCap > 0 ? (usage.credits / settings.monthlyCreditCap) * 100 : 0);

    return (
        <SectionCard title={t('CONTENT_PACKS.SETTINGS.TITLE')} description={t('CONTENT_PACKS.SETTINGS.DESC')} testId="pack-settings">
            <div className="flex flex-col gap-6">
                <SettingsToggleRow
                    id="cp-auto"
                    icon={<Sparkles />}
                    label={t('CONTENT_PACKS.SETTINGS.AUTO')}
                    description={t('CONTENT_PACKS.SETTINGS.AUTO_DESC', { days: settings.windowDays })}
                    checked={settings.auto}
                    disabled={saving}
                    onCheckedChange={(auto) => save({ auto })}
                >
                    <SettingsField label={t('CONTENT_PACKS.SETTINGS.CAP')} htmlFor="cp-cap" hint={t('CONTENT_PACKS.SETTINGS.CAP_HINT')}>
                        <Input
                            id="cp-cap"
                            inputMode="numeric"
                            value={cap}
                            onChange={(e) => setCap(e.target.value.replace(/[^\d]/g, ''))}
                            onBlur={saveCap}
                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                            className="w-32"
                        />
                    </SettingsField>
                    <div className="flex flex-col gap-1.5">
                        <Progress value={used} className="h-1.5" />
                        <p className="text-xs text-muted-foreground">
                            {t('CONTENT_PACKS.SETTINGS.USED', { used: formatNumber(usage.credits), cap: formatNumber(settings.monthlyCreditCap) })}
                        </p>
                    </div>
                </SettingsToggleRow>

                <div className="flex flex-col gap-2">
                    <h3 className="text-xs font-medium text-muted-foreground">{t('CONTENT_PACKS.SETTINGS.KINDS')}</h3>
                    <KindPicker
                        kinds={settings.kinds}
                        onKinds={(kinds) => save({ kinds })}
                        posts={settings.postsPerPack}
                        onPosts={(postsPerPack) => save({ postsPerPack })}
                        prices={estimate.perKind}
                        disabled={saving}
                    />
                    <p className="text-xs text-muted-foreground">{t('CONTENT_PACKS.SETTINGS.PER_PACK', { n: formatNumber(estimate.perPack) })}</p>
                </div>

                <div className="flex flex-col gap-4">
                    <h3 className="text-xs font-medium text-muted-foreground">{t('CONTENT_PACKS.SETTINGS.ON_APPROVE')}</h3>
                    <SettingsToggleRow
                        id="cp-shop"
                        label={t('CONTENT_PACKS.SETTINGS.PUSH_SHOP')}
                        description={
                            shop.canPublish ? (
                                t('CONTENT_PACKS.SETTINGS.SHOP_OK', { platform: platformName(shop.platform) })
                            ) : (
                                <>
                                    {t(`CONTENT_PACKS.PUSH.CODE.${(shop.code || 'not_connected').toUpperCase()}`, { platform: platformName(shop.platform) })}{' '}
                                    <Link to={SHOP_SETTINGS_ROUTE} className="underline underline-offset-2">
                                        {t('CONTENT_PACKS.PUSH.FIX_SHOP')}
                                    </Link>
                                </>
                            )
                        }
                        checked={settings.pushToShop}
                        disabled={saving}
                        onCheckedChange={(pushToShop) => save({ pushToShop })}
                    />
                    <SettingsToggleRow
                        id="cp-social"
                        label={t('CONTENT_PACKS.SETTINGS.PUSH_SOCIAL')}
                        description={
                            social ? (
                                t('CONTENT_PACKS.SETTINGS.SOCIAL_OK', { name: social.accountName || social.platform })
                            ) : (
                                <>
                                    {t('CONTENT_PACKS.SETTINGS.SOCIAL_NONE')}{' '}
                                    <Link to={SOCIAL_ROUTE} className="underline underline-offset-2">
                                        {t('CONTENT_PACKS.PUSH.FIX_SOCIAL')}
                                    </Link>
                                </>
                            )
                        }
                        checked={settings.scheduleSocial}
                        disabled={saving}
                        onCheckedChange={(scheduleSocial) => save({ scheduleSocial })}
                    />
                </div>
            </div>
        </SectionCard>
    );
};
